export interface FilterOrder {
    sortBy: string;
    sortOrder: "DESC" | "ASC" | "asc" | "desc";
}

export const DEFAULT_EL_LIST_SORT: FilterOrder = {
    sortBy: "creationDate",
    sortOrder: "DESC",
};

export const EL_SORT_STORAGE_KEY = {
    titles: "crealo_elementList_sort_titles",
    beneficiaries: "crealo_elementList_sort_beneficiaries",
    works: "crealo_elementList_sort_works",
} as const;

function normalizeSortOrder(value: string): "ASC" | "DESC" {
    return value.toUpperCase() === "ASC" ? "ASC" : "DESC";
}

export function normalizeElementListSortOrder(order: FilterOrder): FilterOrder {
    return {
        ...order,
        sortOrder: normalizeSortOrder(String(order.sortOrder)),
    };
}

export function getPersistedFilterOrder(
    storageKey: string,
    validSortBy: readonly string[],
    fallback: FilterOrder = DEFAULT_EL_LIST_SORT,
): FilterOrder {
    const allowed = new Set(validSortBy);
    try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return fallback;
        const parsed = JSON.parse(raw) as { sortBy?: unknown; sortOrder?: unknown };
        if (typeof parsed.sortBy !== "string" || typeof parsed.sortOrder !== "string") return fallback;
        if (!allowed.has(parsed.sortBy)) return fallback;
        return { sortBy: parsed.sortBy, sortOrder: normalizeSortOrder(parsed.sortOrder) };
    } catch {
        return fallback;
    }
}

/** Initial `chosenOrder` for ElementList: optional persisted value or fallback. */
export function getInitialChosenOrder(
    sortStorageKey: string | undefined,
    orderOptionNames: readonly string[],
    defaultFilterOptions?: FilterOrder,
): FilterOrder {
    const fallback = defaultFilterOptions ?? DEFAULT_EL_LIST_SORT;
    if (!sortStorageKey) return fallback;
    return getPersistedFilterOrder(sortStorageKey, orderOptionNames, fallback);
}

export function persistElementListSort(storageKey: string, order: FilterOrder): void {
    try {
        localStorage.setItem(storageKey, JSON.stringify(order));
    } catch {
        // Quota, private mode, or disabled storage — in-session sort still works via React state.
    }
}
